import type * as React from 'react';
import { type FC, useEffect, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Container, Dialog, Loader, TextField } from '@crehana/ui';
import { useUITheme } from '@crehana/web';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { useHistory } from 'react-router-dom';

import {
  DashboardEmptyView,
  DashboardErrorView,
} from '@/views/UserDashboard/components/DashboardTemplate';
import { useCrehanaTranslation } from '@/views/UserDashboard/hooks';
import {
  ENROLLMENTS_COURSES_ROUTE,
  ENROLLMENTS_ROUTE,
} from '@/views/UserDashboard/routes/config';

import SearchResult from './components/SearchResult';
// import DashboardErrorView from '@/views/UserDashboard/components/DashboardTemplate/DashboardErrorView'
// import DashboardEmptyView from '@/views/UserDashboard/components/DashboardTemplate/DashboardEmptyView'
import GET_SEARCH_DATA_QUERY from './graphql/GetSearchData.graphql';
import type {
  GetUserDashboardSearchData as GetUserDashboardSearchDataResponse,
  GetUserDashboardSearchData_userSearchData as SearchDataItem,
} from './graphql/types/GetUserDashboardSearchData';

interface SearchDialogProps {
  open: boolean;
  onDismiss: () => void;
}

const SearchDialog: FC<React.PropsWithChildren<SearchDialogProps>> = ({
  open,
  onDismiss,
}) => {
  const t = useCrehanaTranslation();

  const history = useHistory();
  const { isDark } = useUITheme();

  const [getSearchData, { called, loading, data, error, refetch }] =
    // biome-ignore lint/complexity/noBannedTypes: type usage acceptable in this context
    useLazyQuery<GetUserDashboardSearchDataResponse, {}>(GET_SEARCH_DATA_QUERY);
  const [queryRetries, setQueryRetries] = useState<number>(0);
  const searchData = data?.userSearchData || [];

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    if (called && !loading && (error || !data) && queryRetries < 3) {
      setQueryRetries(queryRetries + 1);
      if (refetch) {
        refetch();
      }
    }
  }, [called, loading, data, error]);

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<(SearchDataItem | null)[]>(
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    if (open) {
      if (!called) {
        getSearchData();
      }
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    } else {
      setSearchTerm('');
    }
  }, [open]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    if (searchTerm) {
      setSearchResults(
        searchData.filter(item => {
          const term = searchTerm.toLowerCase();

          return (
            item?.title?.toLowerCase().includes(term) ||
            item?.subTitle?.toLowerCase().includes(term)
          );
        }),
      );
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const onSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const renderResults = () => {
    if (!called || loading || !searchTerm) {
      return searchTerm ? (
        <div className="flex justify-center">
          <Loader white={isDark} />
        </div>
      ) : null;
    }
    if ((error || !data) && queryRetries === 3) {
      return (
        <DashboardErrorView
          error={t('UD_SEARCH_ERROR')}
          onRetry={() => {
            if (refetch) {
              refetch();
            }
          }}
          supportsDarkMode
        />
      );
    }
    if (!searchResults.length) {
      return (
        <DashboardEmptyView
          title={t('UD_SEARCH_NO_RESULTS')}
          button={{
            label: t('UD_GO_TO_COURSES'),
            onClick: () => {
              history.push(
                `${ENROLLMENTS_ROUTE.path}${ENROLLMENTS_COURSES_ROUTE.path}`,
              );
              onDismiss();
            },
          }}
          supportsDarkMode
        />
      );
    }
    return (
      <div
        className="w-full h-full overflow-auto flex flex-col pb-24 sm:pb-40"
        data-cy="search-results"
      >
        {searchResults.map((item, i) => (
          <SearchResult
            key={`search-result-${item!.id}`}
            isLast={i === searchResults.length - 1}
            onDismiss={onDismiss}
            {...item!}
          />
        ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onDismiss={onDismiss} size="full" dark={isDark}>
      <Container className="h-screen flex flex-col pt-64 sm:pt-40">
        <TextField
          data-testid="search-course-input"
          className="flex-shrink-0 mb-24 sm:mb-32"
          ref={inputRef}
          hint={t('UD_SEARCH_HINT')}
          value={searchTerm}
          onChange={onSearchTermChange}
          darkMode={isDark}
          autoFocus
          full
        />
        {renderResults()}
      </Container>
    </Dialog>
  );
};

export default SearchDialog;
